import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../lib/utils";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva(
	"inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground dark:bg-stealth-gradient-blue",
	{
		variants: {
			variant: {
				default: "",
				stealth:
					"w-full rounded-none border-b border-stealth-border bg-stealth-gradient-black/40 min-h-12 h-12 p-0",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

const tabsTriggerVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-title font-bold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:data-[state=active]:bg-tooltip dark:text-white dark:data-[state=active]:text-white data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
	{
		variants: {
			variant: {
				default: "",
				stealth:
					"flex-1 text-sm uppercase rounded-none data-[state=active]:bg-stealth-border text-stealth-gray data-[state=active]:text-white h-full",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
		VariantProps<typeof tabsListVariants> & {
			scrollable?: boolean;
		}
>(({ className, variant, scrollable = false, ...props }, ref) => {
	const content = (
		<TabsPrimitive.List
			ref={ref}
			className={cn(tabsListVariants({ variant }), className)}
			{...props}
		/>
	);

	if (scrollable) {
		return (
			<ScrollArea.Root data-scrollable-root="true">
				<ScrollArea.Viewport>{content}</ScrollArea.Viewport>
				<ScrollArea.Scrollbar orientation="horizontal">
					<ScrollArea.Thumb />
				</ScrollArea.Scrollbar>
			</ScrollArea.Root>
		);
	}

	return content;
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
		VariantProps<typeof tabsTriggerVariants>
>(({ className, variant, ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={cn(tabsTriggerVariants({ variant }), className)}
		{...props}
	/>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			"ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
			className,
		)}
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
